"use server";

import { redirect } from "next/navigation";
import { z } from "zod";

import { API_URL } from "@/lib/api/server";
import { ApiError, type ActionState, type SessionUser } from "@/lib/api/types";
import { clearSession, getRefreshToken, setSession } from "@/lib/auth/session";

const loginSchema = z.object({
  email: z.string().min(1, "L'email est requis.").email("Email invalide."),
  password: z.string().min(1, "Le mot de passe est requis.")
});

type LoginResponse = {
  token: string;
  refresh_token?: string;
  user: SessionUser;
};

export async function loginAction(_prev: ActionState, formData: FormData): Promise<ActionState> {
  const parsed = loginSchema.safeParse({
    email: formData.get("email"),
    password: formData.get("password")
  });

  if (!parsed.success) {
    const errors: Record<string, string> = {};
    for (const issue of parsed.error.issues) {
      errors[String(issue.path[0])] = issue.message;
    }
    return { ok: false, errors };
  }

  let data: LoginResponse;

  try {
    const res = await fetch(`${API_URL}/api/v1/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(parsed.data),
      cache: "no-store"
    });

    if (!res.ok) {
      const problem = await res.json().catch(() => ({}));
      return {
        ok: false,
        message:
          res.status === 401
            ? (problem.message ?? "Email ou mot de passe incorrect.")
            : (problem.detail ?? problem.message ?? "Connexion impossible.")
      };
    }

    data = (await res.json()) as LoginResponse;
  } catch {
    return { ok: false, message: "API injoignable. Vérifie que le serveur Symfony tourne." };
  }

  // Le back-office est reserve aux administrateurs. L'API accepte les comptes
  // ROLE_USER (le site public s'en servira), mais aucune session n'est ouverte
  // ici pour eux : sans cette verification ils atterriraient sur un dashboard
  // vide fait d'appels en 403.
  const isAdmin = data.user.roles.some(
    (role) => role === "ROLE_ADMIN" || role === "ROLE_SUPER_ADMIN"
  );

  if (!isAdmin) {
    return {
      ok: false,
      message: "Ce compte n'a pas accès au back-office."
    };
  }

  await setSession({
    accessToken: data.token,
    refreshToken: data.refresh_token,
    user: data.user
  });

  redirect("/dashboard/users");
}

export async function logoutAction() {
  const refreshToken = await getRefreshToken();

  if (refreshToken) {
    // Invalide le refresh token côté API (best effort).
    await fetch(`${API_URL}/api/v1/auth/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
      cache: "no-store"
    }).catch(() => null);
  }

  await clearSession();
  redirect("/dashboard/signin");
}

export async function changePasswordAction(
  _prev: ActionState,
  formData: FormData
): Promise<ActionState> {
  const { apiFetch } = await import("@/lib/api/server");

  const schema = z
    .object({
      currentPassword: z.string().min(1, "Mot de passe actuel requis."),
      newPassword: z.string().min(8, "8 caractères minimum."),
      confirmPassword: z.string()
    })
    .refine((v) => v.newPassword === v.confirmPassword, {
      path: ["confirmPassword"],
      message: "Les mots de passe ne correspondent pas."
    });

  const parsed = schema.safeParse({
    currentPassword: formData.get("currentPassword"),
    newPassword: formData.get("newPassword"),
    confirmPassword: formData.get("confirmPassword")
  });

  if (!parsed.success) {
    const errors: Record<string, string> = {};
    for (const issue of parsed.error.issues) {
      errors[String(issue.path[0])] = issue.message;
    }
    return { ok: false, errors };
  }

  try {
    await apiFetch("/api/v1/me/password", {
      method: "POST",
      body: {
        currentPassword: parsed.data.currentPassword,
        newPassword: parsed.data.newPassword
      }
    });
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message };
    return { ok: false, message: "Erreur inattendue." };
  }

  return { ok: true, message: "Mot de passe mis à jour." };
}
